<template>
  <BenefitsConfirmModal
    :value="value"
    :title="t('benefitsCenter-tiers-deleteModal-title')"
    :description="t('benefitsCenter-tiers-deleteModal-description')"
    :cancel-label="t('benefitsCenter-tiers-deleteModal-cancel')"
    :confirm-label="t('benefitsCenter-tiers-deleteModal-confirm')"
    confirm-icon="ui-trash-01"
    :confirm-disabled="!canConfirm"
    :loading="loading"
    @input="$emit('input', $event)"
    @confirm="confirm"
  >
    <div v-if="tier" class="benefits-tier-delete-body">
      <BenefitsTierCard :tier="tier" />

      <p v-if="impactLoading" class="benefits-tier-delete-status">{{ t("benefitsCenter-tiers-deleteModal-impactLoading") }}</p>
      <p v-else-if="impactError" class="benefits-tier-delete-status is-error">{{ t(impactErrorTerm) }}</p>

      <template v-else-if="impact">
        <p v-if="!hasImpact" class="benefits-tier-delete-status">{{ t("benefitsCenter-tiers-deleteModal-noImpact") }}</p>

        <div v-if="impact.deactivate.length" class="benefits-tier-delete-impact">
          <p>{{ t("benefitsCenter-tiers-deleteModal-deactivateTitle") }}</p>
          <ul>
            <li v-for="reward in impact.deactivate" :key="reward.id">{{ reward.name }}</li>
          </ul>
        </div>

        <div v-if="impact.unlink.length" class="benefits-tier-delete-impact is-neutral">
          <p>{{ t("benefitsCenter-tiers-deleteModal-unlinkTitle") }}</p>
          <ul>
            <li v-for="reward in impact.unlink" :key="reward.id">{{ reward.name }}</li>
          </ul>
        </div>

        <div v-if="impact.automations.length" class="benefits-tier-delete-impact is-neutral">
          <p>{{ t("benefitsCenter-tiers-deleteModal-automationsTitle") }}</p>
          <template v-if="impact.canPauseAutomations">
            <span class="benefits-tier-delete-hint">{{ t("benefitsCenter-tiers-deleteModal-automationsHint") }}</span>
            <ul class="benefits-tier-delete-automations">
              <li v-for="automation in impact.automations" :key="automation.id">
                <label>
                  <input v-model="pauseAutomationIds" type="checkbox" :value="automation.id" />
                  <span>{{ automation.title }}</span>
                </label>
              </li>
            </ul>
          </template>

          <template v-else>
            <span class="benefits-tier-delete-hint">{{ t("benefitsCenter-tiers-deleteModal-automationsNoPermissionHint") }}</span>
            <ul>
              <li v-for="automation in impact.automations" :key="automation.id">{{ automation.title }}</li>
            </ul>
          </template>
        </div>
      </template>

      <confirm-phrase :key="phraseKey" v-model="phraseConfirmed" :placeholder-text="confirmPhrase" color-text="#f04438" />
    </div>
  </BenefitsConfirmModal>
</template>

<script>
import keviApi from "../../../keviApi";
import BenefitsConfirmModal from "../../shared/benefits-confirm-modal.vue";
import ConfirmPhrase from "../../../components/inputs/confirm-phrase.vue";
import BenefitsTierCard from "./benefits-tier-card.vue";

export default {
  name: "BenefitsTierDeleteModal",
  components: {
    BenefitsConfirmModal,
    ConfirmPhrase,
    BenefitsTierCard,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: { type: Boolean, default: false },
    tier: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      phraseConfirmed: false,
      phraseKey: 0,
      impact: null,
      impactLoading: false,
      impactError: null,
      pauseAutomationIds: [],
      requestId: 0,
    };
  },
  computed: {
    confirmPhrase() {
      return this.t("benefitsCenter-tiers-deleteModal-phrase", [String(this.tier?.name || "").toUpperCase()]);
    },
    hasImpact() {
      return !!this.impact && (this.impact.deactivate.length > 0 || this.impact.unlink.length > 0 || this.impact.automations.length > 0);
    },
    impactErrorTerm() {
      return this.impactError === "not-found" ? "benefitsCenter-tiers-errors-tierNotFound" : "benefitsCenter-tiers-deleteModal-impactError";
    },
    canConfirm() {
      return this.phraseConfirmed && !!this.impact && !this.impactLoading;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.phraseConfirmed = false;
        this.phraseKey += 1;
        this.loadImpact();
      }
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    loadImpact() {
      if (!this.tier) return;

      const requestId = ++this.requestId;
      this.impact = null;
      this.impactError = null;
      this.impactLoading = true;
      this.pauseAutomationIds = [];

      this.$http(keviApi.getOptions(`benefits/tiers/${this.tier.id}/deletion-impact`))
        .then((response) => {
          if (requestId !== this.requestId) return;

          const body = response.body || {};
          this.impact = {
            deactivate: (body.rewards && body.rewards.deactivate) || [],
            unlink: (body.rewards && body.rewards.unlink) || [],
            automations: body.automations || [],
            canPauseAutomations: body.canPauseAutomations === true,
          };
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;
          this.impactError = error && error.status === 404 ? "not-found" : "failed";
        })
        .finally(() => {
          if (requestId !== this.requestId) return;
          this.impactLoading = false;
        });
    },
    confirm() {
      if (!this.canConfirm) return;
      const pauseAutomationIds = this.impact.canPauseAutomations ? [...this.pauseAutomationIds] : [];
      this.$emit("confirm", { tier: this.tier, pauseAutomationIds });
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-tier-delete-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
  width: 100%;
}

.benefits-tier-delete-status {
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  margin: 0;

  &.is-error {
    color: #b42318;
  }
}

.benefits-tier-delete-impact {
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 8px;
  color: #b42318;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  padding: 12px 14px;

  &.is-neutral {
    background: #f9fafb;
    border-color: #e5e7eb;
    color: #384250;
  }

  p {
    font-weight: 600;
    margin: 0 0 6px;
  }

  ul {
    margin: 0;
    max-height: 140px;
    overflow-y: auto;
    padding-left: 20px;
  }
}

.benefits-tier-delete-hint {
  color: #6c737f;
  display: block;
  font-size: 13px;
  margin-bottom: 8px;
}

.benefits-tier-delete-automations {
  list-style: none;
  padding-left: 0 !important;

  label {
    align-items: center;
    cursor: pointer;
    display: flex;
    font-weight: 400;
    gap: 8px;
    margin: 0 0 6px;
  }

  input {
    margin: 0;
  }
}
</style>
